import React from 'react';
import {
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

import type {
  PassbookItem,
  PassbookPagination,
} from '../types';

interface SimplePassbookTableProps {
  items: PassbookItem[];
  pagination: PassbookPagination | null;
  loading?: boolean;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onRowClick: (transaction: PassbookItem) => void;
}

const formatMoney = (
  value: number | string | null | undefined,
) =>
  new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const formatDateTime = (value?: string | null) => {
  if (!value) return '—';

  return new Date(value).toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const toNumberOrNull = (
  value: string | number | null | undefined,
): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const getTotalBefore = (item: PassbookItem): number | null => {
  const metadata = item.ledger_metadata;

  const explicitTotal = toNumberOrNull(
    metadata?.player_balance_before,
  );

  if (explicitTotal !== null) {
    return explicitTotal;
  }

  const available = toNumberOrNull(metadata?.available_before);
  const locked = toNumberOrNull(metadata?.locked_before);

  if (available !== null && locked !== null) {
    return available + locked;
  }

  return null;
};

const getTotalAfter = (item: PassbookItem): number | null => {
  const metadata = item.ledger_metadata;

  const explicitTotal = toNumberOrNull(
    metadata?.player_balance_after,
  );

  if (explicitTotal !== null) {
    return explicitTotal;
  }

  const available = toNumberOrNull(metadata?.available_after);
  const locked = toNumberOrNull(metadata?.locked_after);

  if (available !== null && locked !== null) {
    return available + locked;
  }

  return null;
};

const getNetChange = (item: PassbookItem): number | null => {
  const metadata = item.ledger_metadata;

  const explicitChange = toNumberOrNull(
    metadata?.player_net_change,
  );

  if (explicitChange !== null) {
    return explicitChange;
  }

  const before = getTotalBefore(item);
  const after = getTotalAfter(item);

  if (before !== null && after !== null) {
    return after - before;
  }

  return null;
};

const getSelectedNumber = (
  item: PassbookItem,
): string | number | null => {
  const details = item.bet_details;
  const placed = details?.placed_bets || [];
  const first = placed[0];

  const originalBet =
    details?.result_data?.original_payload?.bets?.[0];

  const betTypeName = String(
    item.bet_type?.name ||
      item.ledger_metadata?.bet?.['bet_type_name'] ||
      '',
  )
    .trim()
    .toLowerCase();

  const session = String(
    details?.session ||
      item.ledger_metadata?.bet?.['bet_session'] ||
      '',
  )
    .trim()
    .toLowerCase();

  // Display-only Sangam formatting.
  //
  // Generic Half Sangam:
  // OPEN  = digit-pana
  // CLOSE = pana-digit
  if (betTypeName === 'half sangam') {
    const pana =
      originalBet?.pana ??
      first?.pana ??
      null;

    const digit =
      originalBet?.digit ??
      first?.digit ??
      null;

    if (pana !== null && digit !== null) {
      if (session === 'open') {
        return `${digit}-${pana}`;
      }

      if (session === 'close') {
        return `${pana}-${digit}`;
      }
    }
  }

  // Full Sangam = open_pana-close_pana.
  if (betTypeName === 'full sangam') {
    const openPana =
      originalBet?.open_pana ??
      first?.open_pana ??
      null;

    const closePana =
      originalBet?.close_pana ??
      first?.close_pana ??
      null;

    if (
      openPana !== null &&
      closePana !== null
    ) {
      return `${openPana}-${closePana}`;
    }
  }

  // Preserve the original fallback for every other game.
  return (
    first?.jodi ??
    first?.combination ??
    first?.digit ??
    first?.pana ??
    details?.jodi ??
    details?.close_digit ??
    details?.open_digit ??
    details?.close_pana ??
    details?.open_pana ??
    null
  );
};

const isHistoricalMainBazarRepair = (
  item: PassbookItem,
): boolean =>
  String(
    (item.ledger_metadata as any)?.idempotency_key || '',
  ).startsWith(
    'main_bazar_historical_crossdate_repair_',
  );

const getHistoricalBusinessDate = (
  item: PassbookItem,
): string | null => {
  if (!isHistoricalMainBazarRepair(item)) {
    return null;
  }

  const match = String(item.description || '').match(
    /Business date:\s*(\d{4}-\d{2}-\d{2})/i,
  );

  return match?.[1] || null;
};

const formatBusinessDate = (
  value?: string | null,
): string => {
  if (!value) return '—';

  const [year, month, day] = value
    .split('-')
    .map(Number);

  if (!year || !month || !day) {
    return value;
  }

  return new Date(
    Date.UTC(year, month - 1, day),
  ).toLocaleDateString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const getOriginalBetCreatedAt = (
  item: PassbookItem,
): string | null =>
  String(
    item.ledger_metadata?.bet?.['created_at'] || '',
  ) || null;

const buildRemark = (
  item: PassbookItem,
): string => {
  const transactionType = String(
    item.transaction_type || '',
  ).toLowerCase();

  const historicalRepair =
    isHistoricalMainBazarRepair(item);

  const historicalBusinessDate =
    getHistoricalBusinessDate(item);

  if (
    transactionType === 'deposit' ||
    transactionType === 'withdrawal_completed' ||
    transactionType === 'withdrawal' ||
    transactionType === 'refund' ||
    transactionType === 'reconciliation'
  ) {
    return (
      item.description ||
      item.type_display ||
      item.transaction_type ||
      'Transaction'
    );
  }

  const marketName =
    item.market?.name ||
    item.ledger_metadata?.bet?.['market_name'] ||
    item.ledger_metadata?.starline_bet?.[
      'market_name'
    ] ||
    '';

  const betTypeName =
    item.bet_type?.name ||
    item.ledger_metadata?.bet?.['bet_type_name'] ||
    item.ledger_metadata?.starline_bet?.[
      'bet_type_name'
    ] ||
    '';

  const session =
    item.bet_details?.session ||
    item.ledger_metadata?.bet?.['bet_session'] ||
    item.ledger_metadata?.starline_bet?.[
      'session'
    ] ||
    '';

  const selectedNumber =
    getSelectedNumber(item);

  const change = Math.abs(
    getNetChange(item) ?? 0,
  );

  const amountForRemark =
    transactionType === 'bet_won'
      ? Number(item.win_amount || 0) || change
      : Number(item.bet_amount || 0) ||
        Math.abs(Number(item.amount || 0)) ||
        change;

  const parts = [
    marketName
      ? String(marketName).toUpperCase()
      : null,
    betTypeName
      ? String(betTypeName).toUpperCase()
      : null,
    session
      ? String(session).toUpperCase()
      : null,
  ].filter(Boolean);

  if (parts.length > 0) {
    const betContext = `${parts.join(' - ')}${
      selectedNumber !== null
        ? ` : ${selectedNumber}`
        : ''
    }`;

    if (transactionType === 'bet_placed') {
      return `BET PLACED • ₹${formatMoney(
        amountForRemark,
      )} - ${betContext}`;
    }

    if (transactionType === 'bet_lost') {
      if (historicalRepair) {
        return `ACCOUNT CORRECTION${
          historicalBusinessDate
            ? ` • ORIGINAL BET ${formatBusinessDate(
                historicalBusinessDate,
              )}`
            : ''
        } • LOST ₹${formatMoney(
          amountForRemark,
        )} STAKE • ${betContext}`;
      }

      return `SETTLEMENT LOST • ₹${formatMoney(
        amountForRemark,
      )} STAKE - ${betContext}`;
    }

    if (transactionType === 'bet_won') {
      if (historicalRepair) {
        const netCorrection =
          getNetChange(item);

        return `ACCOUNT CORRECTION${
          historicalBusinessDate
            ? ` • ORIGINAL BET ${formatBusinessDate(
                historicalBusinessDate,
              )}`
            : ''
        } • WON ₹${formatMoney(
          amountForRemark,
        )} PAYOUT • NET ${
          netCorrection !== null && netCorrection > 0 ? '+' : ''
        }${netCorrection === null
          ? '—'
          : `₹${formatMoney(netCorrection)}`} • ${betContext}`;
      }

      return `SETTLEMENT WON • ₹${formatMoney(
        amountForRemark,
      )} PAYOUT - ${betContext}`;
    }

    return `₹${formatMoney(
      amountForRemark,
    )} - ${betContext}`;
  }

  return (
    item.description ||
    item.type_display ||
    item.transaction_type ||
    'Transaction'
  );
};

const getVisiblePages = (
  currentPage: number,
  totalPages: number,
): Array<number | string> => {
  if (totalPages <= 7) {
    return Array.from(
      { length: totalPages },
      (_, index) => index + 1,
    );
  }

  if (currentPage <= 4) {
    return [
      1,
      2,
      3,
      4,
      5,
      'ellipsis-right',
      totalPages,
    ];
  }

  if (currentPage >= totalPages - 3) {
    return [
      1,
      'ellipsis-left',
      totalPages - 4,
      totalPages - 3,
      totalPages - 2,
      totalPages - 1,
      totalPages,
    ];
  }

  return [
    1,
    'ellipsis-left',
    currentPage - 1,
    currentPage,
    currentPage + 1,
    'ellipsis-right',
    totalPages,
  ];
};

const SimplePassbookTable: React.FC<
  SimplePassbookTableProps
> = ({
  items,
  pagination,
  loading = false,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onRowClick,
}) => {
  const visibleItems = items;

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-col gap-3 border-b border-slate-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">
            Wallet Ledger
          </h2>

          <p className="mt-1 text-sm text-slate-500">
            Complete financial activity for this player.
          </p>
        </div>

        {pagination && (
          <p className="text-sm text-slate-500">
            {pagination.total_count.toLocaleString(
              'en-IN',
            )}{' '}
            transactions
          </p>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full border-collapse text-sm">
          <thead className="bg-slate-50">
            <tr>
              {[
                'Sr.No',
                'Total Before',
                'Total Change',
                'Total After',
                'Remark',
                'Created',
              ].map((heading) => (
                <th
                  key={heading}
                  scope="col"
                  className={`border border-slate-200 px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 ${
                    heading === 'Remark'
                      ? 'text-left'
                      : 'text-center'
                  }`}
                >
                  {heading}
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="bg-white">
            {loading ? (
              <tr>
                <td
                  colSpan={6}
                  className="border border-slate-200 px-4 py-12 text-center text-sm text-slate-500"
                >
                  Loading financial ledger...
                </td>
              </tr>
            ) : visibleItems.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  className="border border-slate-200 px-4 py-12 text-center text-sm text-slate-500"
                >
                  No financial transactions found.
                </td>
              </tr>
            ) : (
              visibleItems.map(
                (
                  item,
                  index,
                  allVisibleItems,
                ) => {
                  const before =
                    getTotalBefore(item);

                  const after =
                    getTotalAfter(item);

                  const change =
                    getNetChange(item);

                  const historicalRepair =
                    isHistoricalMainBazarRepair(item);

                  const originalBetCreatedAt =
                    historicalRepair
                      ? getOriginalBetCreatedAt(item)
                      : null;

                  const serial =
                    pagination
                      ? pagination.total_count -
                        ((pagination.current_page -
                          1) *
                          pageSize) -
                        index
                      : allVisibleItems.length -
                        index;

                  return (
                    <tr
                      key={item.id}
                      onClick={() =>
                        onRowClick(item)
                      }
                      className="cursor-pointer transition hover:bg-slate-50"
                    >
                      <td className="border border-slate-200 px-4 py-2.5 text-center font-medium text-slate-700">
                        {serial}
                      </td>

                      <td className="border border-slate-200 px-4 py-2.5 text-center text-slate-700">
                        {before === null
                          ? '—'
                          : formatMoney(before)}
                      </td>

                      <td
                        className={`border border-slate-200 px-4 py-2.5 text-center font-semibold ${
                          change !== null && change > 0
                            ? 'text-emerald-600'
                            : change !== null && change < 0
                              ? 'text-red-600'
                              : 'text-slate-500'
                        }`}
                      >
                        {change !== null && change > 0 ? '+' : ''}
                        {change === null ? '—' : formatMoney(change)}
                      </td>

                      <td className="border border-slate-200 px-4 py-2.5 text-center text-slate-700">
                        {after === null
                          ? '—'
                          : formatMoney(after)}
                      </td>

                      <td className="whitespace-nowrap border border-slate-200 px-4 py-2.5 text-left text-slate-700">
                        {buildRemark(item)}
                      </td>

                      <td className="whitespace-nowrap border border-slate-200 px-4 py-2.5 text-center text-slate-600">
                        {historicalRepair ? (
                          <div className="space-y-1 text-xs">
                            <div>
                              <span className="font-semibold text-slate-700">
                                Original bet:
                              </span>{' '}
                              {originalBetCreatedAt
                                ? formatDateTime(
                                    originalBetCreatedAt,
                                  )
                                : formatBusinessDate(
                                    getHistoricalBusinessDate(
                                      item,
                                    ),
                                  )}
                            </div>
                            <div className="text-slate-500">
                              <span className="font-semibold">
                                Corrected:
                              </span>{' '}
                              {formatDateTime(
                                item.created_at,
                              )}
                            </div>
                          </div>
                        ) : (
                          formatDateTime(
                            item.created_at,
                          )
                        )}
                      </td>
                    </tr>
                  );
                },
              )
            )}
          </tbody>
        </table>
      </div>

      {pagination && (
        <div className="flex flex-col gap-3 border-t border-slate-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm text-slate-500">
            Showing page{' '}
            {pagination.current_page} of{' '}
            {pagination.total_pages} ·{' '}
            {pagination.total_count.toLocaleString(
              'en-IN',
            )}{' '}
            transactions
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            {pagination.total_pages > 1 && (
              <div className="flex flex-wrap items-center gap-1">
                <button
                  type="button"
                  disabled={
                    loading ||
                    !pagination.has_previous
                  }
                  onClick={() =>
                    onPageChange(
                      Math.max(
                        1,
                        pagination.current_page -
                          1,
                      ),
                    )
                  }
                  className="inline-flex items-center gap-1 rounded-lg px-2.5 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </button>

                {getVisiblePages(
                  pagination.current_page,
                  pagination.total_pages,
                ).map((page) => {
                  if (
                    page === 'ellipsis-left' ||
                    page === 'ellipsis-right'
                  ) {
                    return (
                      <span
                        key={page}
                        className="px-2 py-2 text-sm text-slate-400"
                      >
                        …
                      </span>
                    );
                  }

                  const isCurrent =
                    page ===
                    pagination.current_page;

                  return (
                    <button
                      key={page}
                      type="button"
                      disabled={
                        loading || isCurrent
                      }
                      onClick={() =>
                        onPageChange(
                          Number(page),
                        )
                      }
                      className={`min-w-9 rounded-lg px-3 py-2 text-sm font-medium transition ${
                        isCurrent
                          ? 'bg-blue-600 text-white shadow-sm'
                          : 'text-slate-700 hover:bg-slate-100'
                      } disabled:cursor-default`}
                    >
                      {page}
                    </button>
                  );
                })}

                <button
                  type="button"
                  disabled={
                    loading ||
                    !pagination.has_next
                  }
                  onClick={() =>
                    onPageChange(
                      pagination.current_page +
                        1,
                    )
                  }
                  className="inline-flex items-center gap-1 rounded-lg px-2.5 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            )}

            <select
              value={pageSize}
              disabled={loading}
              onChange={(event) =>
                onPageSizeChange(
                  Number(
                    event.target.value,
                  ),
                )
              }
              aria-label="Transactions per page"
              className="h-10 rounded-lg border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {[10, 20, 25, 50, 100].map(
                (size) => (
                  <option
                    key={size}
                    value={size}
                  >
                    {size} per page
                  </option>
                ),
              )}
            </select>
          </div>
        </div>
      )}
    </div>
  );
};

export default SimplePassbookTable;
